import axios, { HttpStatusCode } from 'axios';
import { decodeJWT } from '@aws-amplify/core';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { AuthApi } from '@bvengineering/cas-member-gateway-service-client';
import { ENV } from './env';

const EXPIRY_SAFETY_WINDOW_MS = 1000;

type TokenRefresher = Parameters<
  typeof cognitoUserPoolsTokenProvider.tokenOrchestrator.setTokenRefresher
>[0];
type CognitoTokens = Awaited<ReturnType<TokenRefresher>>;

const refreshControllers = new Set<AbortController>();
const refreshedTokenGenerations = new WeakMap<CognitoTokens, number>();
type GetTokensResult = ReturnType<typeof cognitoUserPoolsTokenProvider.tokenOrchestrator.getTokens>;
let getTokensInFlight: GetTokensResult | undefined;
const pendingTokenReads = new Set<GetTokensResult>();
let sessionGeneration = 0;
let pausedTransitions = 0;
let persistenceGuardRegistered = false;
let getTokensGuardRegistered = false;

function cancelledRefresh(): Error {
  const error = new Error('The session changed while refreshing tokens.');
  error.name = 'GatewayRefreshCancelled';
  return error;
}

export async function withGatewayRefreshPaused<Result>(
  operation: () => Promise<Result>
): Promise<Result> {
  pausedTransitions += 1;
  sessionGeneration += 1;
  for (const controller of refreshControllers) controller.abort();
  try {
    while (pendingTokenReads.size > 0) {
      await Promise.allSettled([...pendingTokenReads]);
    }
    getTokensInFlight = undefined;
    return await operation();
  } finally {
    sessionGeneration += 1;
    try {
      while (pendingTokenReads.size > 0) {
        await Promise.allSettled([...pendingTokenReads]);
      }
      getTokensInFlight = undefined;
    } finally {
      pausedTransitions -= 1;
    }
  }
}

// Gateway requires an expired access token, so Amplify forceRefresh waits for expiry too.
async function waitUntilAccessTokenExpires(
  args: Parameters<TokenRefresher>[0],
  signal: AbortSignal
): Promise<void> {
  const expiresAt = args.tokens.accessToken.payload.exp;
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) {
    throw new Error('The creator access token has no valid expiration time.');
  }

  const expirationTime = expiresAt * 1000 - (args.tokens.clockDrift ?? 0) + EXPIRY_SAFETY_WINDOW_MS;
  const delay = expirationTime - Date.now();
  if (delay > 0) {
    await new Promise<void>((resolve, reject) => {
      const cancel = () => {
        clearTimeout(timer);
        reject(cancelledRefresh());
      };
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', cancel);
        resolve();
      }, delay);
      signal.addEventListener('abort', cancel, { once: true });
      if (signal.aborted) cancel();
    });
  }
}

async function refreshWithGateway(args: Parameters<TokenRefresher>[0]): Promise<CognitoTokens> {
  if (pausedTransitions > 0) throw cancelledRefresh();
  const refreshToken = args.tokens.refreshToken;
  if (!refreshToken) {
    throw new Error('The creator session has no refresh token.');
  }
  const generation = sessionGeneration;
  const controller = new AbortController();
  refreshControllers.add(controller);

  const refreshPromise = (async () => {
    await waitUntilAccessTokenExpires(args, controller.signal);
    if (generation !== sessionGeneration) throw cancelledRefresh();

    if (!ENV.memberGatewayBasePath) {
      throw new Error('The Member Gateway base URL is not configured.');
    }

    let response;
    try {
      const basePath = ENV.memberGatewayBasePath.replace(/\/+$/, '');
      const authApi = new AuthApi(undefined, basePath, axios.create());
      response = await authApi.creatorsAuthControllerRefreshToken(
        {
          accessToken: args.tokens.accessToken.toString(),
          refreshToken,
        },
        { signal: controller.signal }
      );
    } catch (error) {
      if (generation !== sessionGeneration) throw cancelledRefresh();
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized) {
        const authError = new Error('The creator refresh token was rejected.');
        authError.name = 'NotAuthorizedException';
        throw authError;
      }
      throw error;
    }

    if (generation !== sessionGeneration) throw cancelledRefresh();

    const refreshed = response.data;
    if (
      !refreshed.accessToken ||
      !refreshed.idToken ||
      !refreshed.refreshToken ||
      !refreshed.tokenType ||
      !Number.isFinite(refreshed.expiresIn) ||
      refreshed.expiresIn <= 0
    ) {
      throw new Error('The Member Gateway returned an incomplete token set.');
    }

    const accessToken = decodeJWT(refreshed.accessToken);
    const idToken = decodeJWT(refreshed.idToken);
    const issuedAt = accessToken.payload.iat;
    if (typeof issuedAt !== 'number' || !Number.isFinite(issuedAt)) {
      throw new Error('The refreshed access token has no valid issued-at time.');
    }

    const tokens: CognitoTokens = {
      accessToken,
      idToken,
      refreshToken: refreshed.refreshToken,
      username: args.username,
      clockDrift: issuedAt * 1000 - Date.now(),
      deviceMetadata: args.tokens.deviceMetadata,
    };
    refreshedTokenGenerations.set(tokens, generation);
    return tokens;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshControllers.delete(controller);
  }
}

export const createAmplifyTokenRefresher = (): TokenRefresher => refreshWithGateway;

export function registerAmplifyTokenRefresher(): void {
  const orchestrator = cognitoUserPoolsTokenProvider.tokenOrchestrator;
  if (!getTokensGuardRegistered) {
    const getTokens = orchestrator.getTokens.bind(orchestrator);
    orchestrator.getTokens = (options) => {
      if (getTokensInFlight) return getTokensInFlight;

      const generation = sessionGeneration;
      const request = getTokens(options).then((tokens) => {
        if (generation !== sessionGeneration) throw cancelledRefresh();
        return tokens;
      });
      getTokensInFlight = request;
      pendingTokenReads.add(request);
      void request
        .finally(() => {
          pendingTokenReads.delete(request);
          if (getTokensInFlight === request) getTokensInFlight = undefined;
        })
        .catch(() => {});
      return request;
    };
    getTokensGuardRegistered = true;
  }
  if (!persistenceGuardRegistered) {
    const setTokens = orchestrator.setTokens.bind(orchestrator);
    orchestrator.setTokens = async (input) => {
      const generation = refreshedTokenGenerations.get(input.tokens);
      if (generation === undefined) return setTokens(input);
      if (generation !== sessionGeneration || pausedTransitions > 0) throw cancelledRefresh();
      await setTokens(input);
      if (generation !== sessionGeneration) throw cancelledRefresh();
    };
    persistenceGuardRegistered = true;
  }
  orchestrator.setTokenRefresher(createAmplifyTokenRefresher());
}
